import * as React from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Store, Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Redirect, RouteComponentProps, match } from 'react-router-dom';
import { Venues, LandingPages } from '@weddingspot/ws-api-client';
import { WSAppContext, WSAppContextType, SpinnerOverlay } from '@weddingspot/ws-core-components';
import { logEvent } from '@weddingspot/ws-analytics';
import { AnalyticsEvent } from '@weddingspot/ws-analytics';
import { getUserInfo, setUserInfo, AuthAction } from '../../actions/auth';
import {
    LoadLandingPageData,
    loadLandingPageData,
    loadLandingPageRecommendedVenues,
    landingPageLoaded,
    LandingPagesAction,
} from '../../actions/landingPages';
import * as types from '../../types';

import GuideLandingPage from './GuideLandingPage';
import { addServerError } from '../../actions/errors';
import AppLogger from '../../utils/logging';
const logger = AppLogger.createLogger();

const styles = require('./GuideLandingPage.scss');

interface OwnProps extends RouteComponentProps<{ guide: string }> {}

interface ActionProps {
    load: (path: string) => Promise<LandingPages.LandingPageData>;
    setLoaded: () => void;
    loadRecommendations: (venueIds: number[]) => Promise<(Venues.VenueDetails | Venues.UnlistedVenueDetails)[]>;
    getUserInfo: () => Promise<{ isStaff: boolean }>;
}

interface StateProps {
    data: types.LandingPageState;
    guide: string;
    changeUrl: (path: string) => void;
}

type Props = StateProps & ActionProps & StateProps & OwnProps;

const mapStateToProps = (state: types.StoreState, ownProps: OwnProps): StateProps => ({
    data: state.landingPageData,
    guide: ownProps.match.params.guide,
    changeUrl: (path: string) => (window.location.href = path),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<types.StoreState, undefined, LoadLandingPageData & AuthAction>): ActionProps => ({
    load: (path: string) => dispatch(loadLandingPageData(path)),
    setLoaded: () => dispatch(landingPageLoaded()),
    loadRecommendations: (venueIds: number[]) => dispatch(loadLandingPageRecommendedVenues(venueIds)),
    getUserInfo: () =>
        getUserInfo().then((userInfo) => {
            dispatch(setUserInfo(userInfo));
            return userInfo;
        }),
});

export class GuideLandingPageContainer extends React.Component<Props> {
    static contextType: React.Context<WSAppContextType> = WSAppContext;
    context!: React.ContextType<typeof WSAppContext>;

    static fetchData(store: Store<types.StoreState>, match: match<{ guide: string }>): Promise<any> {
        return new Promise((resolve, reject) => {
            const storeDispatch = store.dispatch as ThunkDispatch<types.StoreState, undefined, Action>;
            storeDispatch(loadLandingPageData(match.params.guide))
                .then((res) => {
                    let ids: number[] = [];
                    if (res.carousel_venue_ids !== undefined) {
                        ids = res.carousel_venue_ids.split(',').map((n) => parseInt(n.trim(), 10));
                    }
                    storeDispatch(loadLandingPageRecommendedVenues(ids))
                        .then(() => {
                            storeDispatch(landingPageLoaded());
                        })
                        .catch((err) => {
                            logger.error(err);
                            storeDispatch(landingPageLoaded()); // second request not important, even if fall we need to show page
                        })
                        .then(resolve);
                })
                .catch((err) => {
                    storeDispatch(addServerError(err.status));
                    reject(err);
                });
        });
    }

    componentDidMount() {
        this.props.getUserInfo().then(this.loadData).catch(this.loadData);
    }

    loadData = () => {
        if (this.props.data.name === '') {
            // Not fetched on SSR, so have to load data here
            this.props
                .load(this.props.guide)
                .then((res) => {
                    let ids: number[] = [];
                    if (res.carousel_venue_ids !== undefined) {
                        ids = res.carousel_venue_ids.split(',').map((n) => parseInt(n.trim(), 10));
                    }
                    if (this.context.user?.isStaff || this.props.data.published) {
                        this.props.loadRecommendations(ids).then(() => this.props.setLoaded());
                    }
                })
                .catch((err) => {
                    logger.error(err);
                });
        } else if (this.props.data.published || this.context.user?.isStaff) {
            this.props.setLoaded();
        }
    };

    logVisitedEvent = () => {
        logEvent(AnalyticsEvent.EVTNM_VISITED_LANDING_PAGE, {
            Market: this.props.data.market,
            Region: this.props.data.region,
        });
    };

    render() {
        return !this.props.data.isLoaded ? (
            <div className={styles.loadingWrapper}>
                <SpinnerOverlay isSpinning={true} />
            </div>
        ) : (
            <React.Fragment>
                <Helmet>
                    <title>
                        {this.props.data.meta_title_override
                            ? this.props.data.meta_title_override
                            : this.props.data.region + ' Destination Weddings & Packages | Wedding Spot'}
                    </title>
                    <link rel='canonical' href={this.props.location.pathname} />
                </Helmet>
                <GuideLandingPage
                    data={this.props.data}
                    onVenueListingClick={(link: string, position: number) => {
                        logEvent(
                            AnalyticsEvent.EVTNM_CLICKED_DW_LANDING_PAGE_LISTING,
                            {
                                Market: this.props.data.market,
                                Region: this.props.data.region,
                                Position: position,
                            },
                            () => (window.location.href = link)
                        );
                    }}
                    onSeeAllButtonClick={(link) => {
                        logEvent(AnalyticsEvent.EVTNM_CLICKED_DW_LANDING_PAGE_SEE_ALL, {}, () => (window.location.href = link));
                    }}
                    onHeroPhotoClick={(link, photoOrder) => {
                        logEvent(
                            AnalyticsEvent.EVTNM_CLICKED_DW_HERO_PHOTO,
                            {
                                Market: this.props.data.market,
                                Region: this.props.data.region,
                                'Photo order': photoOrder,
                            },
                            () => (window.location.href = link)
                        );
                    }}
                    onLandingPageClick={(link, region) => {
                        logEvent(
                            AnalyticsEvent.EVTNM_CLICKED_DW_LANDING_PAGE_LINK,
                            {
                                'To Market': 'Destination Weddings',
                                'To Region': region,
                                Source: 'landing page',
                            },
                            () => (window.location.href = link)
                        );
                    }}
                />
            </React.Fragment>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(GuideLandingPageContainer);
